import React, { useState, useMemo } from 'react'
import { groupColor } from './theme'
import { formatInches } from './formatters'

/**
 * All Parts — a consolidated table view across groups with search, filters,
 * bulk edit, and highlight sync with the interactive SVG on the group tab.
 */
export default function NesterPartsTable({
  colors,
  parts,
  materialGroups,
  highlightedPartId,
  onHighlightPart,
  onUpdateField,
  onUpdateQuantity,
  onDeletePart,
  onBatchUpdate,
  fillResolutions = {},
}) {
  const [query, setQuery] = useState('')
  const [groupFilter, setGroupFilter] = useState('all')
  const [sortKey, setSortKey] = useState('file_name')
  const [sortDir, setSortDir] = useState('asc')
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [showBulk, setShowBulk] = useState(false)

  const filtered = useMemo(() => {
    let out = parts
    if (groupFilter !== 'all') {
      if (groupFilter === 'unassigned') out = out.filter(p => !p.material_group_id)
      else out = out.filter(p => String(p.material_group_id) === groupFilter)
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase()
      out = out.filter(p =>
        (p.part_label || p.file_name || '').toLowerCase().includes(q)
        || (p.file_name || '').toLowerCase().includes(q)
      )
    }
    out = [...out].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey]
      if (sortKey === 'file_name') { av = a.part_label || a.file_name || ''; bv = b.part_label || b.file_name || '' }
      if (av == null) av = ''
      if (bv == null) bv = ''
      if (typeof av === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      }
      return sortDir === 'asc' ? (av - bv) : (bv - av)
    })
    return out
  }, [parts, query, groupFilter, sortKey, sortDir])

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key); setSortDir('asc')
    }
  }

  function toggleSelected(id) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  function selectAll() {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set())
    else setSelectedIds(new Set(filtered.map(p => p.part_id)))
  }

  const selectedCount = selectedIds.size
  const groupMap = useMemo(() => {
    const m = new Map()
    materialGroups.forEach((g, i) => m.set(g.group_id, { group: g, idx: i }))
    return m
  }, [materialGroups])

  return (
    <div style={{ padding: 16 }}>
      {/* Toolbar */}
      <div style={styles.toolbar(colors)}>
        <input value={query} onChange={e => setQuery(e.target.value)}
          placeholder="🔍 Search by filename or label…"
          style={{ ...styles.input(colors), width: 280 }} />
        {materialGroups.length > 0 && (
          <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
            style={styles.input(colors)}>
            <option value="all">All groups</option>
            <option value="unassigned">Unassigned</option>
            {materialGroups.map((g, i) => (
              <option key={g.group_id} value={String(g.group_id)}>
                {g.group_name || `Group ${i + 1}`} — {g.material_type || ''} {g.thickness_display || ''}
              </option>
            ))}
          </select>
        )}
        <div style={{ fontSize: 12, color: colors.textMuted }}>
          {filtered.length} of {parts.length} parts
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowBulk(v => !v)}
          disabled={selectedCount === 0}
          style={{
            ...styles.btn(colors),
            background: selectedCount > 0 ? colors.accent : colors.panelAlt,
            color: selectedCount > 0 ? '#fff' : colors.textMuted,
            cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
          }}>
          Bulk edit ({selectedCount})
        </button>
      </div>

      {showBulk && selectedCount > 0 && (
        <BulkEditBar
          colors={colors}
          selectedIds={[...selectedIds]}
          materialGroups={materialGroups}
          onApply={async (patch) => {
            if (onBatchUpdate) {
              await onBatchUpdate([...selectedIds].map(id => ({ part_id: id, ...patch })))
            } else if (onUpdateField) {
              for (const id of selectedIds) {
                await onUpdateField(id, patch)
              }
            }
            setSelectedIds(new Set())
            setShowBulk(false)
          }}
          onCancel={() => setShowBulk(false)}
        />
      )}

      {/* Table */}
      <div style={styles.tableWrap(colors)}>
        <table style={styles.table(colors)}>
          <thead>
            <tr style={styles.thead(colors)}>
              <th style={{ ...styles.th(colors), width: 34 }}>
                <input type="checkbox" onChange={selectAll}
                  checked={filtered.length > 0 && selectedIds.size === filtered.length} />
              </th>
              <th style={{ ...styles.th(colors), width: 48 }}>Preview</th>
              <SortTh label="Name" k="file_name" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} colors={colors} />
              {materialGroups.length > 0 && <th style={styles.th(colors)}>Group</th>}
              <SortTh label="Size" k="bounding_width" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} colors={colors} numeric />
              <SortTh label="Qty" k="quantity" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} colors={colors} numeric />
              <SortTh label="Cut" k="cut_length_in" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} colors={colors} numeric />
              <SortTh label="Pierces" k="pierce_count" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} colors={colors} numeric />
              <th style={styles.th(colors)}>Rot</th>
              <th style={styles.th(colors)}>CL</th>
              <th style={styles.th(colors)}>Tabs</th>
              <th style={styles.th(colors)}>Fill</th>
              <th style={{ ...styles.th(colors), width: 40 }}>×</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => {
              const highlighted = String(p.part_id) === String(highlightedPartId)
              const grp = p.material_group_id ? groupMap.get(p.material_group_id) : null
              const fillRes = fillResolutions[p.part_id]
              return (
                <tr key={p.part_id}
                  style={{
                    background: highlighted ? colors.accentSoft : 'transparent',
                    cursor: 'pointer',
                    borderLeft: grp ? `3px solid ${groupColor(grp.idx)}` : '3px solid transparent',
                  }}
                  onClick={() => onHighlightPart?.(highlighted ? null : p.part_id)}>
                  <td style={styles.td(colors)} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedIds.has(p.part_id)}
                      onChange={() => toggleSelected(p.part_id)} />
                  </td>
                  <td style={styles.td(colors)}>
                    {p.svg_data && (
                      <div style={{ width: 32, height: 32, overflow: 'hidden' }}
                        dangerouslySetInnerHTML={{ __html: p.svg_data }} />
                    )}
                  </td>
                  <td style={styles.td(colors)} title={p.file_name}>
                    <div style={{ fontWeight: highlighted ? 700 : 500, color: colors.text }}>
                      {p.part_label || p.file_name}
                    </div>
                    {p.warnings?.length > 0 && (
                      <div style={{ fontSize: 10, color: colors.warn }}>⚠ {p.warnings.join('; ')}</div>
                    )}
                  </td>
                  {materialGroups.length > 0 && (
                    <td style={styles.td(colors)} onClick={e => e.stopPropagation()}>
                      <select value={p.material_group_id || ''}
                        onChange={e => onUpdateField(p.part_id, { material_group_id: e.target.value ? parseInt(e.target.value) : null })}
                        style={{ ...styles.inlineInput(colors), width: 140,
                          borderColor: grp ? groupColor(grp.idx) : colors.warn }}>
                        <option value="">— unassigned —</option>
                        {materialGroups.map((g, i) => (
                          <option key={g.group_id} value={g.group_id}>
                            {g.group_name || `Group ${i + 1}`}
                          </option>
                        ))}
                      </select>
                    </td>
                  )}
                  <td style={{ ...styles.td(colors), textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                    {p.bounding_width?.toFixed(2)} × {p.bounding_height?.toFixed(2)}
                  </td>
                  <td style={styles.td(colors)} onClick={e => e.stopPropagation()}>
                    {p.fill_sheet ? (
                      <span style={{ fontSize: 11, color: colors.accent, fontWeight: 600 }}
                        title="Auto-filled by Fill Sheet">
                        {fillRes != null ? fillRes : '(fill)'}
                      </span>
                    ) : (
                      <input type="number" min="1" value={p.quantity}
                        onChange={e => onUpdateQuantity(p.part_id, parseInt(e.target.value) || 1)}
                        style={{ ...styles.inlineInput(colors), width: 54, textAlign: 'right' }} />
                    )}
                  </td>
                  <td style={{ ...styles.td(colors), textAlign: 'right', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                    {formatInches(p.cut_length_in, 1)}
                  </td>
                  <td style={{ ...styles.td(colors), textAlign: 'right', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                    {p.pierce_count}
                  </td>
                  <td style={styles.td(colors)} onClick={e => e.stopPropagation()}>
                    <select value={p.part_rotation ?? ''}
                      onChange={e => onUpdateField(p.part_id, { part_rotation: e.target.value === '' ? null : parseInt(e.target.value) })}
                      style={{ ...styles.inlineInput(colors), width: 72 }}>
                      <option value="">Auto</option>
                      <option value="0">0°</option>
                      <option value="90">90°</option>
                      <option value="180">180°</option>
                      <option value="270">270°</option>
                    </select>
                  </td>
                  <td style={styles.td(colors)} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={!!p.common_line}
                      onChange={e => onUpdateField(p.part_id, { common_line: e.target.checked })} />
                  </td>
                  <td style={styles.td(colors)} onClick={e => e.stopPropagation()}>
                    <input type="number" min="0" max="20" value={p.microtabs || ''}
                      placeholder="0"
                      onChange={e => onUpdateField(p.part_id, { microtabs: parseInt(e.target.value) || 0 })}
                      style={{ ...styles.inlineInput(colors), width: 48 }} />
                  </td>
                  <td style={styles.td(colors)} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={!!p.fill_sheet}
                      onChange={e => onUpdateField(p.part_id, { fill_sheet: e.target.checked })} />
                  </td>
                  <td style={styles.td(colors)} onClick={e => e.stopPropagation()}>
                    <button onClick={() => onDeletePart(p.part_id)}
                      style={styles.deleteBtn(colors)}>×</button>
                  </td>
                </tr>
              )
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan="99" style={{ ...styles.td(colors), textAlign: 'center', color: colors.textMuted, padding: 24 }}>
                  No parts match your filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function SortTh({ label, k, sortKey, sortDir, onClick, colors, numeric }) {
  const active = sortKey === k
  return (
    <th style={{
      ...styles.th(colors),
      textAlign: numeric ? 'right' : 'left',
      cursor: 'pointer',
      userSelect: 'none',
    }}
      onClick={() => onClick(k)}>
      {label}
      {active && <span style={{ marginLeft: 4, fontSize: 10 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  )
}

function BulkEditBar({ colors, selectedIds, materialGroups, onApply, onCancel }) {
  const [qty, setQty] = useState('')
  const [rotation, setRotation] = useState('')
  const [cl, setCl] = useState('')
  const [groupId, setGroupId] = useState('')
  const [microtabs, setMicrotabs] = useState('')

  const patch = {}
  if (qty !== '') patch.quantity = parseInt(qty) || 1
  if (rotation !== '') patch.part_rotation = rotation === 'auto' ? null : parseInt(rotation)
  if (cl !== '') patch.common_line = cl === 'true'
  if (groupId !== '') patch.material_group_id = groupId === 'none' ? null : parseInt(groupId)
  if (microtabs !== '') patch.microtabs = parseInt(microtabs) || 0

  const hasPatch = Object.keys(patch).length > 0

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      padding: '10px 12px', marginBottom: 10,
      background: colors.accentSoft,
      border: `1px solid ${colors.accentBorder}`,
      borderRadius: 6,
      fontSize: 12,
    }}>
      <span style={{ fontWeight: 700, color: colors.text }}>
        Apply to {selectedIds.length} part{selectedIds.length === 1 ? '' : 's'}:
      </span>
      <label>Qty: <input type="number" min="1" value={qty} onChange={e => setQty(e.target.value)}
        style={{ ...styles.inlineInput(colors), width: 56 }} /></label>
      <label>Rotation: <select value={rotation} onChange={e => setRotation(e.target.value)} style={{ ...styles.inlineInput(colors), width: 86 }}>
        <option value="">— skip —</option>
        <option value="auto">Auto</option>
        <option value="0">0°</option><option value="90">90°</option><option value="180">180°</option><option value="270">270°</option>
      </select></label>
      <label>CL: <select value={cl} onChange={e => setCl(e.target.value)} style={{ ...styles.inlineInput(colors), width: 74 }}>
        <option value="">— skip —</option>
        <option value="true">On</option>
        <option value="false">Off</option>
      </select></label>
      <label>Tabs: <input type="number" min="0" max="20" value={microtabs} onChange={e => setMicrotabs(e.target.value)}
        style={{ ...styles.inlineInput(colors), width: 54 }} /></label>
      {materialGroups.length > 0 && (
        <label>Group: <select value={groupId} onChange={e => setGroupId(e.target.value)} style={{ ...styles.inlineInput(colors), width: 160 }}>
          <option value="">— skip —</option>
          <option value="none">Unassigned</option>
          {materialGroups.map((g, i) => (
            <option key={g.group_id} value={g.group_id}>
              {g.group_name || `Group ${i + 1}`}
            </option>
          ))}
        </select></label>
      )}
      <div style={{ flex: 1 }} />
      <button onClick={() => onApply(patch)} disabled={!hasPatch}
        style={{
          ...styles.btn(colors),
          background: hasPatch ? colors.accent : colors.panelAlt,
          color: hasPatch ? '#fff' : colors.textMuted,
          cursor: hasPatch ? 'pointer' : 'not-allowed',
        }}>
        Apply
      </button>
      <button onClick={onCancel} style={styles.btn(colors)}>Cancel</button>
    </div>
  )
}

const styles = {
  toolbar: (c) => ({
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    marginBottom: 10,
  }),
  input: (c) => ({
    padding: '5px 10px', fontSize: 13,
    border: `1px solid ${c.border}`, borderRadius: 4,
    background: c.panel, color: c.text,
  }),
  inlineInput: (c) => ({
    padding: '2px 6px', fontSize: 12,
    border: `1px solid ${c.border}`, borderRadius: 3,
    background: c.panel, color: c.text,
    boxSizing: 'border-box',
  }),
  btn: (c) => ({
    padding: '4px 12px', borderRadius: 4,
    border: `1px solid ${c.border}`, background: c.panel, color: c.text,
    fontSize: 12, fontWeight: 600,
    cursor: 'pointer',
  }),
  deleteBtn: (c) => ({
    padding: '1px 8px', fontSize: 13,
    border: `1px solid ${c.danger}`, color: c.danger,
    background: 'transparent', borderRadius: 3,
    cursor: 'pointer', fontWeight: 700,
  }),
  tableWrap: (c) => ({
    overflowX: 'auto',
    border: `1px solid ${c.border}`, borderRadius: 6,
    background: c.panel,
  }),
  table: (c) => ({
    width: '100%', borderCollapse: 'collapse', fontSize: 13,
  }),
  thead: (c) => ({
    background: c.panelAlt,
  }),
  th: (c) => ({
    padding: '6px 10px', textAlign: 'left',
    fontSize: 11, fontWeight: 700, color: c.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: `1px solid ${c.border}`,
    position: 'sticky', top: 0, background: c.panelAlt,
  }),
  td: (c) => ({
    padding: '4px 10px',
    borderBottom: `1px solid ${c.border}`,
    color: c.text,
  }),
}
